/**
 * Secure File Access Utilities
 * 
 * This module provides functions for securely accessing uploaded files
 * through signed URLs instead of direct file paths.
 * 
 * Files are no longer directly accessible via /uploads/* URLs.
 * Instead, they must be accessed through /api/files/view/:token
 */

// Get proper API base URL for file signing requests
const getApiBaseUrl = (): string => {
  const rawApiUrl = process.env.REACT_APP_API_URL || '';
  
  // If we have a full URL (http://...), extract the origin
  if (rawApiUrl.startsWith('http')) {
    try {
      const parsed = new URL(rawApiUrl);
      return `${parsed.protocol}//${parsed.host}`;
    } catch {
      // Fall through to development check
    }
  }
  
  // Check if we're in development (localhost with frontend on port 3000)
  const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  if (isDevelopment && window.location.port === '3000') {
    // Development: backend is on port 5000
    return `${window.location.protocol}//${window.location.hostname}:5000`;
  }
  
  // Production or same-origin: use current origin
  return window.location.origin;
};

const API_BASE_URL = getApiBaseUrl();

interface SignedUrlResponse {
  success: boolean;
  signedUrl?: string;
  expiresIn?: number;
  message?: string;
}

interface BatchSignedUrlResponse {
  success: boolean;
  signedUrls?: Array<{
    filePath: string;
    signedUrl: string | null;
    expiresIn?: number;
    error?: string;
  }>;
  message?: string;
}

// Cache for signed URLs to avoid unnecessary API calls
const signedUrlCache = new Map<string, { url: string; expiresAt: number }>();

/**
 * Get a signed URL for a file path
 * @param filePath - The original file path (e.g., /uploads/...)
 * @param applicationId - Optional application ID for ownership verification
 * @returns Promise resolving to the signed URL or null if failed
 */
export async function getSignedFileUrl(
  filePath: string,
  applicationId?: string
): Promise<string | null> {
  if (!filePath) return null;
  
  // Check if it's a public static file (templates, brochures, etc.)
  if (isPublicStaticFile(filePath)) {
    // Public files don't need signing
    return filePath.startsWith('http') ? filePath : `${window.location.origin}${filePath}`;
  }
  
  // Check cache first
  const cacheKey = `${filePath}:${applicationId || ''}`;
  const cached = signedUrlCache.get(cacheKey);
  if (cached && cached.expiresAt > Date.now() + 60000) { // 1 minute buffer
    return cached.url;
  }
  
  try {
    const response = await fetch(`${API_BASE_URL}/api/files/sign`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include cookies for authentication
      body: JSON.stringify({ filePath, applicationId }),
    });
    
    const data: SignedUrlResponse = await response.json();
    
    if (data.success && data.signedUrl) {
      // Build full URL
      const fullUrl = data.signedUrl.startsWith('http') 
        ? data.signedUrl 
        : `${API_BASE_URL}${data.signedUrl}`;
      
      // Cache the signed URL
      signedUrlCache.set(cacheKey, {
        url: fullUrl,
        expiresAt: Date.now() + ((data.expiresIn || 1800) * 1000), // Default 30 min
      });
      
      return fullUrl;
    }
    
    console.error('[SecureFile] Failed to get signed URL:', data.message);
    return null;
  } catch (error) {
    console.error('[SecureFile] Error getting signed URL:', error);
    return null;
  }
}

/**
 * Get signed URLs for multiple files in a single request
 * @param files - Array of file paths with optional application IDs
 * @returns Promise resolving to a map of original paths to signed URLs
 */
export async function getSignedFileUrlsBatch(
  files: Array<{ filePath: string; applicationId?: string }>
): Promise<Map<string, string | null>> {
  const result = new Map<string, string | null>();
  
  // Separate public files from private files
  const publicFiles: typeof files = [];
  const privateFiles: typeof files = [];
  
  for (const file of files) {
    if (isPublicStaticFile(file.filePath)) {
      publicFiles.push(file);
    } else {
      privateFiles.push(file);
    }
  }
  
  // Handle public files immediately
  for (const file of publicFiles) {
    const url = file.filePath.startsWith('http') 
      ? file.filePath 
      : `${window.location.origin}${file.filePath}`;
    result.set(file.filePath, url);
  }
  
  // Return early if no private files
  if (privateFiles.length === 0) {
    return result;
  }
  
  // Check cache and identify uncached files
  const uncachedFiles: typeof files = [];
  for (const file of privateFiles) {
    const cacheKey = `${file.filePath}:${file.applicationId || ''}`;
    const cached = signedUrlCache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now() + 60000) {
      result.set(file.filePath, cached.url);
    } else {
      uncachedFiles.push(file);
    }
  }
  
  // Fetch uncached files from API
  if (uncachedFiles.length > 0) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/files/sign-batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ files: uncachedFiles }),
      });
      
      const data: BatchSignedUrlResponse = await response.json();
      
      if (data.success && data.signedUrls) {
        for (const item of data.signedUrls) {
          if (item.signedUrl) {
            const fullUrl = item.signedUrl.startsWith('http')
              ? item.signedUrl
              : `${API_BASE_URL}${item.signedUrl}`;
            
            // Cache the URL
            const cacheKey = `${item.filePath}:${uncachedFiles.find(f => f.filePath === item.filePath)?.applicationId || ''}`;
            signedUrlCache.set(cacheKey, {
              url: fullUrl,
              expiresAt: Date.now() + ((item.expiresIn || 1800) * 1000),
            });
            
            result.set(item.filePath, fullUrl);
          } else {
            result.set(item.filePath, null);
          }
        }
      }
    } catch (error) {
      console.error('[SecureFile] Error getting batch signed URLs:', error);
      // Set null for all uncached files on error
      for (const file of uncachedFiles) {
        result.set(file.filePath, null);
      }
    }
  }
  
  return result;
}

/**
 * Check if a file path is a public static file (doesn't need signing)
 */
export function isPublicStaticFile(filePath: string): boolean {
  if (!filePath) return false;
  
  const publicPaths = [
    '/appendix_all/',
    '/brochures/',
    '/templates/',
    '/reference/',
    '/assets/',
    '/images/',
  ];
  
  return publicPaths.some(prefix => filePath.includes(prefix));
}

/**
 * Check if a file path is an uploaded file (needs signing)
 */
export function isUploadedFile(filePath: string): boolean {
  if (!filePath) return false;
  return filePath.includes('/uploads/') || filePath.startsWith('uploads/');
}

/**
 * Clear the signed URL cache (useful on logout)
 */
export function clearSignedUrlCache(): void {
  signedUrlCache.clear();
}

/**
 * Get file URL - handles both public and private files appropriately
 * This is the main function to use for displaying files
 * 
 * @param filePath - The file path
 * @param applicationId - Optional application ID
 * @returns Promise resolving to the appropriate URL
 */
export async function getSecureFileUrl(
  filePath: string,
  applicationId?: string
): Promise<string | null> {
  if (!filePath) return null;
  
  // If already a full URL with our API path, return as-is
  if (filePath.includes('/api/files/view/')) {
    return filePath;
  }
  
  // For public static files, return direct URL
  if (isPublicStaticFile(filePath)) {
    if (filePath.startsWith('http')) return filePath;
    return `${window.location.origin}${filePath.startsWith('/') ? '' : '/'}${filePath}`;
  }
  
  // For uploaded files, get signed URL
  if (isUploadedFile(filePath)) {
    return await getSignedFileUrl(filePath, applicationId);
  }
  
  // Default: return as-is
  return filePath;
}
